import { useEffect, useMemo, useState } from "react";
import { ArrowUpToLine, ArrowDownToLine, GripVertical, Maximize2, Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, media, CATEGORIES } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const Opt = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`border px-3 py-2 text-xs ${active ? "border-white bg-white text-black" : "border-white/20 text-white/80 hover:border-white/50"}`}>{children}</button>
);

export const GalleryArranger = () => {
  const { settings, setSettings } = useLang();
  const [cat, setCat] = useState(CATEGORIES[0]);
  const [items, setItems] = useState([]);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [layouts, setLayouts] = useState(settings.gallery_layouts || {});
  useEffect(() => { setLayouts(settings.gallery_layouts || {}); }, [settings.gallery_layouts]);
  useEffect(() => {
    setDirty(false);
    api.get(`/admin/photos?category=${cat}`).then((r) => setItems((Array.isArray(r.data) ? r.data : []).filter((p) => p.status === "ready").sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))).catch(() => {});
  }, [cat]);
  const L = layouts[cat] || { layout: "justified", rowSize: "m", columns: 3, aspect: "3/2" };
  const setL = (patch) => setLayouts((x) => ({ ...x, [cat]: { ...L, ...patch } }));
  const moveTo = (from, to) => {
    if (from === to || from == null || to == null) return;
    setItems((xs) => { const a = [...xs]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; });
    setDirty(true);
  };
  const toggleWide = async (p) => {
    setItems((xs) => xs.map((x) => (x.id === p.id ? { ...x, wide: !x.wide } : x)));
    await api.patch(`/admin/photos/${p.id}`, { wide: !p.wide }).catch((e) => toast.error(errMsg(e)));
  };
  const save = async () => {
    try {
      await api.post("/admin/photos/reorder", { ids: items.map((p) => p.id) });
      const r = await api.put("/admin/settings", { ...settings, gallery_layouts: layouts });
      setSettings(r.data); setDirty(false);
      toast.success("Gallery saved — the public page now shows this order");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const ratio = (p) => (p.variants?.thumb ? p.variants.thumb.width / p.variants.thumb.height : 1.5);
  const preview = useMemo(() => items, [items]);
  return (
    <div data-testid="gallery-arranger">
      <div className="mb-5 flex flex-wrap gap-2">
        {CATEGORIES.map((c) => <Opt key={c} active={cat === c} onClick={() => setCat(c)} testid={`arranger-cat-${c}`}>{c}</Opt>)}
      </div>
      <div className="mb-6 grid gap-5 border border-white/10 bg-[#0f0f0f] p-5 md:grid-cols-3">
        <div>
          <div className="kicker mb-2">Layout</div>
          <div className="flex flex-wrap gap-2">
            <Opt active={L.layout !== "grid"} onClick={() => { setL({ layout: "justified" }); setDirty(true); }} testid="arranger-layout-justified">Natural proportions</Opt>
            <Opt active={L.layout === "grid"} onClick={() => { setL({ layout: "grid" }); setDirty(true); }} testid="arranger-layout-grid">Uniform grid</Opt>
          </div>
          <p className="mt-2 text-[11px] text-white/50">{L.layout === "grid" ? "Same-size tiles, photos cropped to fit." : "Rows of uncropped photos, read left to right."}</p>
        </div>
        {L.layout === "grid" ? (<>
          <div><div className="kicker mb-2">Columns</div><div className="flex gap-2">{[2, 3, 4].map((n) => <Opt key={n} active={Number(L.columns) === n} onClick={() => { setL({ columns: n }); setDirty(true); }} testid={`arranger-cols-${n}`}>{n}</Opt>)}</div></div>
          <div><div className="kicker mb-2">Tile shape</div><div className="flex gap-2">{[["3/2", "Landscape"], ["4/5", "Portrait"], ["1/1", "Square"]].map(([k, l]) => <Opt key={k} active={L.aspect === k} onClick={() => { setL({ aspect: k }); setDirty(true); }} testid={`arranger-aspect-${k.replace("/", "-")}`}>{l}</Opt>)}</div></div>
        </>) : (
          <div><div className="kicker mb-2">Row height</div><div className="flex gap-2">{[["s", "Compact"], ["m", "Medium"], ["l", "Large"]].map(([k, l]) => <Opt key={k} active={(L.rowSize || "m") === k} onClick={() => { setL({ rowSize: k }); setDirty(true); }} testid={`arranger-row-${k}`}>{l}</Opt>)}</div></div>
        )}
      </div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-white/65">Drag photos into the order you want (first = top-left). <Maximize2 size={12} className="inline" /> makes a photo span wide.</p>
        <div className="flex gap-2">
          <a href={`/gallery/${cat}`} target="_blank" rel="noreferrer" data-testid="arranger-view-page" className="flex items-center gap-1.5 border border-white/20 px-4 py-2.5 text-sm text-white"><ExternalLink size={14} /> View page</a>
          <button onClick={save} data-testid="arranger-save-button" className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold ${dirty ? "bg-[#5d9a35] text-white" : "bg-white text-black"}`}><Save size={14} /> {dirty ? "Save changes" : "Saved"}</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3" data-testid="arranger-grid">
        {preview.map((p, i) => (
          <div key={p.id} draggable onDragStart={() => setDrag(i)} onDragEnd={() => { setDrag(null); setOver(null); }}
            onDragOver={(e) => { e.preventDefault(); setOver(i); }} onDrop={(e) => { e.preventDefault(); moveTo(drag, i); setDrag(null); setOver(null); }}
            data-testid={`arranger-item-${i}`}
            className={`group relative cursor-grab border-2 bg-[#111] transition-[opacity,border-color] active:cursor-grabbing ${drag === i ? "opacity-40" : ""} ${over === i && drag !== i ? "border-[#5d9a35]" : "border-transparent"}`}
            style={{ height: 150, width: p.wide ? 150 * ratio(p) * 1.6 : 150 * ratio(p) }}>
            {p.urls?.thumb && <img src={media(p.urls.thumb)} alt={p.title} draggable={false} className="h-full w-full object-cover" />}
            <span className="absolute left-1 top-1 bg-black/75 px-1.5 font-mono text-[11px] text-white">{i + 1}</span>
            <GripVertical size={14} className="absolute right-1 top-1 text-white/70" />
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/75 px-1.5 py-1 opacity-100 md:opacity-0 md:group-hover:opacity-100">
              <span className="truncate text-[10px] text-white">{p.title}</span>
              <span className="flex shrink-0 gap-1 text-white/80">
                <button onClick={() => moveTo(i, 0)} title="Move to first" data-testid={`arranger-top-${i}`} className="hover:text-white"><ArrowUpToLine size={13} /></button>
                <button onClick={() => moveTo(i, items.length - 1)} title="Move to last" data-testid={`arranger-bottom-${i}`} className="hover:text-white"><ArrowDownToLine size={13} /></button>
                <button onClick={() => toggleWide(p)} title="Wide tile" data-testid={`arranger-wide-${i}`} className={p.wide ? "text-[#5d9a35]" : "hover:text-white"}><Maximize2 size={13} /></button>
              </span>
            </div>
          </div>
        ))}
      </div>
      {!items.length && <p className="text-sm text-white/55">No published photos in this gallery yet.</p>}
    </div>
  );
};
